import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { categories } from '../../data/mockData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    brand_name: '',
    website: '',
    category: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setIsSubmitted(true);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        brand_name: '',
        website: '',
        category: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { num: '01', text: 'Tell Us What You Need' },
    { num: '02', text: 'Collaborate with Our Experts' },
    { num: '03', text: 'Receive a Free Quote' },
    { num: '04', text: 'Kick Off Your Project' },
  ];

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')]" />
      
      {/* Decorative Orbs */}
      <div className="absolute top-20 right-20 w-96 h-96 bg-[#ef4444]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-20 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-[#ef4444]" />
            <span className="text-sm font-semibold text-white">Get Professional Guidance</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-white">Get <span className="gradient-text">Started</span></h2>
          <p className="text-slate-400 mt-3 text-lg">Get Started With Zaroori Retail Today</p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Side - Contact Info */}
          <div className="space-y-8">
            {/* Contact Cards - Glassmorphism */}
            <div className="grid sm:grid-cols-2 gap-4">
              <a href="tel:+917009008425" className="glass rounded-2xl p-6 flex items-center gap-4 hover:bg-white/20 transition-colors group">
                <div className="w-12 h-12 bg-gradient-to-br from-[#ef4444] to-[#dc2626] rounded-xl flex items-center justify-center red-glow-sm">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Call Us</p>
                  <p className="text-white font-semibold group-hover:text-[#ef4444] transition-colors">+91 7009008425</p>
                </div>
              </a>
              
              <a href="mailto:business@zarooriretail.com" className="glass rounded-2xl p-6 flex items-center gap-4 hover:bg-white/20 transition-colors group">
                <div className="w-12 h-12 bg-gradient-to-br from-[#ef4444] to-[#dc2626] rounded-xl flex items-center justify-center red-glow-sm">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Email Us</p>
                  <p className="text-white font-semibold group-hover:text-[#ef4444] transition-colors text-sm">business@zarooriretail.com</p>
                </div>
              </a>
            </div>
            
            <div className="glass rounded-2xl p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-[#ef4444] to-[#dc2626] rounded-xl flex items-center justify-center red-glow-sm">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Our Offices</p>
                <p className="text-white font-semibold">Ludhiana | Noida | Dubai | USA</p>
              </div>
            </div>
            
            {/* Process Steps */}
            <div className="space-y-4">
              {steps.map((step, i) => (
                <div key={i} className="flex items-center gap-4 group">
                  <div className="w-10 h-10 glass rounded-full flex items-center justify-center text-white font-bold text-sm group-hover:bg-gradient-to-br group-hover:from-[#ef4444] group-hover:to-[#dc2626] transition-all">
                    {step.num}
                  </div>
                  <p className="text-white/80 group-hover:text-white transition-colors font-medium">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
          
          {/* Right Side - Form with Neomorphism */}
          <div className="neo-shadow-lg bg-white rounded-3xl p-8">
            <h3 className="text-2xl font-extrabold text-slate-800 mb-2">Let's <span className="gradient-text">Connect!</span></h3>
            <p className="text-slate-500 mb-8">Send us a message, and we'll promptly discuss your project with you.</p>
            
            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 neo-shadow bg-gradient-to-br from-green-400 to-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
                <h4 className="text-2xl font-extrabold text-slate-800 mb-2">Thank You!</h4>
                <p className="text-slate-500">We've received your message and will get back to you soon.</p>
                <Button 
                  onClick={() => setIsSubmitted(false)}
                  className="mt-6 bg-gradient-to-r from-[#ef4444] to-[#dc2626] hover:from-[#dc2626] hover:to-[#b91c1c] rounded-2xl neo-shadow-sm"
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Input 
                      placeholder="First Name" 
                      value={formData.first_name}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      required
                      className="neo-inset-sm rounded-xl border-0 bg-slate-50 focus:ring-2 focus:ring-[#ef4444] placeholder:text-slate-400"
                    />
                  </div>
                  <div>
                    <Input 
                      placeholder="Last Name" 
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      required
                      className="neo-inset-sm rounded-xl border-0 bg-slate-50 focus:ring-2 focus:ring-[#ef4444] placeholder:text-slate-400"
                    />
                  </div>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input 
                    type="email" 
                    placeholder="Email" 
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    className="neo-inset-sm rounded-xl border-0 bg-slate-50 focus:ring-2 focus:ring-[#ef4444] placeholder:text-slate-400"
                  />
                  <Input 
                    type="tel" 
                    placeholder="Phone Number" 
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                    className="neo-inset-sm rounded-xl border-0 bg-slate-50 focus:ring-2 focus:ring-[#ef4444] placeholder:text-slate-400"
                  />
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input 
                    placeholder="Brand Name" 
                    value={formData.brand_name}
                    onChange={(e) => setFormData({...formData, brand_name: e.target.value})}
                    required
                    className="neo-inset-sm rounded-xl border-0 bg-slate-50 focus:ring-2 focus:ring-[#ef4444] placeholder:text-slate-400"
                  />
                  <Input 
                    placeholder="Website (optional)" 
                    value={formData.website}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                    className="neo-inset-sm rounded-xl border-0 bg-slate-50 focus:ring-2 focus:ring-[#ef4444] placeholder:text-slate-400"
                  />
                </div>
                
                <Select onValueChange={(value) => setFormData({...formData, category: value})}>
                  <SelectTrigger className="neo-inset-sm rounded-xl border-0 bg-slate-50 focus:ring-2 focus:ring-[#ef4444]">
                    <SelectValue placeholder="Select Category" />
                  </SelectTrigger>
                  <SelectContent className="neo-shadow bg-white rounded-xl">
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat} className="rounded-lg">{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Textarea 
                  placeholder="Your Message" 
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  required
                  rows={4}
                  className="neo-inset-sm rounded-xl border-0 bg-slate-50 focus:ring-2 focus:ring-[#ef4444] resize-none placeholder:text-slate-400"
                />
                
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#ef4444] to-[#dc2626] hover:from-[#dc2626] hover:to-[#b91c1c] text-white rounded-2xl py-6 text-base font-bold neo-shadow red-glow-sm hover:scale-[1.02] transition-all"
                >
                  {isSubmitting ? 'Sending...' : 'Send Request'}
                  <Send className="w-4 h-4 ml-2" />
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
